// ============================================================
// src/components/LevelPage.jsx
// Muestra los 4 niveles y sus 5 temas con estado de progreso
// ============================================================

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ref, get } from 'firebase/database';
import { database } from '../services/firebase';

import fisicaData from '../data/fisica_openmind.json';
import quimicaData from '../data/quimica_openmind.json';
import biologiaData from '../data/biologia_openmind.json';
import historiaData from '../data/historia_openmind.json';
import politicaData from '../data/politica_openmind.json';
import arteData from '../data/arte_openmind.json';

const ALL_SUBJECTS = [
  { ...fisicaData, color: 'from-blue-500 to-cyan-500', light: 'bg-blue-50', border: 'border-blue-200', accent: 'text-blue-600' },
  { ...quimicaData, color: 'from-emerald-500 to-green-500', light: 'bg-emerald-50', border: 'border-emerald-200', accent: 'text-emerald-600' },
  { ...biologiaData, color: 'from-purple-500 to-violet-500', light: 'bg-purple-50', border: 'border-purple-200', accent: 'text-purple-600' },
  { ...historiaData, color: 'from-amber-500 to-orange-500', light: 'bg-amber-50', border: 'border-amber-200', accent: 'text-amber-600' },
  { ...politicaData, color: 'from-red-500 to-rose-500', light: 'bg-red-50', border: 'border-red-200', accent: 'text-red-600' },
  { ...arteData, color: 'from-pink-500 to-fuchsia-500', light: 'bg-pink-50', border: 'border-pink-200', accent: 'text-pink-600' },
];

const DIFFICULTY = {
  easy: { label: 'Fácil', style: 'bg-green-100 text-green-700' },
  medium: { label: 'Intermedio', style: 'bg-amber-100 text-amber-700' },
  hard: { label: 'Avanzado', style: 'bg-red-100 text-red-700' },
  expert: { label: 'Experto', style: 'bg-purple-100 text-purple-700' },
};

export default function LevelPage({ user }) {
  const { subject: subjectKey } = useParams();
  const navigate = useNavigate();
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);

  // Encontrar la materia activa
  const subject = ALL_SUBJECTS.find((s) => s.subject === subjectKey);

  useEffect(() => {
    if (!user || !subject) return;
    loadProgress();
  }, [user, subjectKey]);

  const loadProgress = async () => {
    try {
      const snap = await get(ref(database, `users/${user.uid}/progress/${subjectKey}`));
      if (snap.exists()) setProgress(snap.val());
    } catch (err) {
      console.error('Error cargando progreso:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!subject) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-400">Materia no encontrada.</p>
      </div>
    );
  }

  // Determina si un nivel está desbloqueado
  // Nivel 1 siempre abierto; los siguientes requieren ≥60% del anterior
  const isLevelUnlocked = (levelIndex) => {
    if (levelIndex === 0) return true;
    const prevTopics = subject.levels[levelIndex - 1].topics;
    const done = prevTopics.filter((t) => progress[t.topicId]?.completed).length;
    return done >= Math.ceil(prevTopics.length * 0.6);
  };

  // Progreso de un nivel
  const levelProgress = (level) => {
    const done = level.topics.filter((t) => progress[t.topicId]?.completed).length;
    return { done, total: level.topics.length, pct: Math.round((done / level.topics.length) * 100) };
  };

  // Totales de la materia
  const allTopics = subject.levels.flatMap((l) => l.topics);
  const totalDone = allTopics.filter((t) => progress[t.topicId]?.completed).length;
  const totalPct = Math.round((totalDone / allTopics.length) * 100);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Header de la materia ─────────────────────────── */}
      <div className={`bg-gradient-to-r ${subject.color} text-white px-4 py-10`}>
        <div className="max-w-3xl mx-auto">
          <button
            onClick={() => navigate('/')}
            className="text-white/70 hover:text-white text-sm mb-4 flex items-center gap-1 transition"
          >
            ← Volver al inicio
          </button>
          <div className="flex items-center gap-4">
            <span className="text-6xl">{subject.icon}</span>
            <div className="flex-1">
              <h1 className="text-3xl md:text-4xl font-extrabold">{subject.displayName}</h1>
              <p className="text-white/75 mt-1 text-sm max-w-xl">{subject.description}</p>
            </div>
          </div>
          {/* Barra de progreso total */}
          <div className="mt-5">
            <div className="flex justify-between text-xs text-white/70 mb-1">
              <span>{totalDone}/{allTopics.length} temas completados</span>
              <span>{totalPct}%</span>
            </div>
            <div className="bg-white/20 rounded-full h-3">
              <div
                className="bg-white h-3 rounded-full transition-all duration-700"
                style={{ width: `${totalPct}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Niveles ──────────────────────────────────────── */}
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {loading ? (
          <div className="text-center py-16 text-gray-400">Cargando progreso...</div>
        ) : (
          subject.levels.map((level, idx) => {
            const unlocked = isLevelUnlocked(idx);
            const lp = levelProgress(level);

            return (
              <div
                key={level.levelNumber}
                className={`rounded-2xl border-2 overflow-hidden transition-all
                  ${unlocked ? 'bg-white border-gray-200 shadow-sm' : 'bg-gray-50 border-gray-100 opacity-60'}`}
              >
                {/* Cabecera del nivel */}
                <div className={`px-5 py-4 flex items-center justify-between
                  ${unlocked ? `bg-gradient-to-r ${subject.color} text-white` : 'bg-gray-100 text-gray-400'}`}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-extrabold text-lg">
                        Nivel {level.levelNumber}: {level.levelName}
                      </span>
                      {!unlocked && (
                        <span className="text-xs bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full">
                          🔒 Completa el nivel anterior
                        </span>
                      )}
                    </div>
                    <p className={`text-sm ${unlocked ? 'text-white/75' : 'text-gray-400'}`}>
                      {level.levelDescription}
                    </p>
                  </div>
                  {unlocked && (
                    <div className="text-right text-white">
                      <div className="font-bold">{lp.done}/{lp.total}</div>
                      <div className="text-xs text-white/70">completados</div>
                    </div>
                  )}
                </div>

                {/* Lista de temas */}
                <div className="divide-y divide-gray-50">
                  {level.topics.map((topic, tIdx) => {
                    const tp = progress[topic.topicId] || {};
                    const done = tp.completed || false;
                    const started = Object.keys(tp.practiceAnswers || {}).length > 0;
                    const score = tp.score || 0;
                    const diff = DIFFICULTY[topic.difficulty] || DIFFICULTY.easy;

                    return (
                      <button
                        key={topic.topicId}
                        disabled={!unlocked}
                        onClick={() => navigate(`/lesson/${subjectKey}/${idx}/${topic.topicId}`)}
                        className={`w-full text-left px-5 py-4 flex items-center gap-4 transition group
                          ${unlocked ? 'hover:bg-indigo-50 cursor-pointer' : 'cursor-not-allowed'}`}
                      >
                        {/* Estado */}
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-base flex-shrink-0
                          ${done ? 'bg-green-100 text-green-600'
                            : started ? 'bg-amber-100 text-amber-600'
                            : unlocked ? 'bg-gray-100 text-gray-500'
                            : 'bg-gray-100 text-gray-300'}`}
                        >
                          {done ? '✅' : started ? '⚡' : unlocked ? (tIdx + 1) : '🔒'}
                        </div>

                        {/* Título y badge */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`font-semibold truncate
                              ${unlocked ? 'text-gray-800 group-hover:text-indigo-700' : 'text-gray-400'} transition`}
                            >
                              {topic.title}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${diff.style}`}>
                              {diff.label}
                            </span>
                          </div>
                          {done && (
                            <p className="text-xs text-green-600 mt-0.5">
                              {score}/40 puntos obtenidos
                            </p>
                          )}
                          {started && !done && (
                            <p className="text-xs text-amber-500 mt-0.5">En progreso…</p>
                          )}
                        </div>

                        {/* Puntos */}
                        <div className="flex-shrink-0 text-right">
                          <span className={`text-sm font-bold ${done ? 'text-green-600' : 'text-gray-300'}`}>
                            {done ? score : '40'} pts
                          </span>
                        </div>

                        {unlocked && (
                          <span className="text-gray-300 group-hover:text-indigo-400 transition">→</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
